let { nothing, html, ref, createRef, CustomHTMLElement } = lithtml;
const tag     = 'wave-markdown';
const version = '0.9.20';

/** @type {Promise}  */
let scriptPromise;

// Ripped from: https://thewebdev.info/2021/06/27/how-to-find-the-first-scrollable-parent-element-with-javascript
let getScrollParentEl = (el) => {
    if (el === null) {
      return null;
    }

    return el.scrollHeight > el.clientHeight ? el : getScrollParentEl(el.parentElement);
  }

/**
 * WaveMarkdown ..
 */
class WaveMarkdown extends CustomHTMLElement {
    static get tag()     { return tag }
    static get version() { return version }

    /** Make sure changing the attributes and properties trigger a rerender */
    static get observedAttributes() { return ['md-source', 'use-shadow-root']; }
    static get observedProperties() { return ['markdownData', '_aT']; }

    /**
     * Constructor
     */
    constructor() {
        super(WaveMarkdown, { useShadowRoot: false });

        if (!scriptPromise) {
            scriptPromise = new Promise((resolve) => {
                // If someone else has added the marked library, make sure we do not download it again
                if (window.marked) {
                    resolve();
                    return;
                }

                let script = document.createElement('script');

                script.type        = 'text/javascript';
                script.src         = 'https://cdn.geminisuite.com/scripts/marked/4.0.14/marked.min.js';
                script.id          = 'wave-markdown-script';
                script.crossOrigin = 'anonymous';
                script.onload      = resolve;

                // INFO integrity: https://report-uri.com/home/sri_hash
                script.integrity = 'sha512-8eLX5I782rMdjOqsAVN5qPj9IsGPzThynM/WG3ruYZparaVhZVX+KVRWHKhmpXVnzFj0LgQMTZtKkTZ+n7wscw==';

                document.head.appendChild(script);

                if (!window.hljs) {
                    script = document.createElement('script');

                    script.type        = 'text/javascript';
                    script.src         = 'https://cdn.geminisuite.com/scripts/highlight/11.7.0/highlight.min.js';
                    script.id          = 'wave-markdown-highlight-script';
                    script.crossOrigin = 'anonymous';
                    //script.onload      = resolve;

                    // INFO integrity: https://report-uri.com/home/sri_hash
                    //script.integrity = 'sha512-8eLX5I782rMdjOqsAVN5qPj9IsGPzThynM/WG3ruYZparaVhZVX+KVRWHKhmpXVnzFj0LgQMTZtKkTZ+n7wscw==';

                    document.head.appendChild(script);

                    let link = document.createElement('link')

                    link.rel         = 'stylesheet';
                    link.href        = 'https://cdn.geminisuite.com/scripts/highlight/11.7.0/a11y-dark.min.css';
                    link.id          = 'wave-markdown-highlight-style';
                    link.crossOrigin = 'anonymous';

                    document.head.appendChild(link);
                }
            });
        }

        this._aU = createRef();
    }

    /** Called when destroy has been called on the element, so we do some cleanup */
    onDestroy () {
        this._aU = null;
    }

    /**
     * Called when an cmdSource changes value
     * @param {string} oldValue the old value
     * @param {string} newValue the new value
     */
    onChanged_mdSource (oldValue, newValue) {
        if (!newValue) {
            return;
        }

        let self = this;
        let hash = self.mdSource.split('#')[1];

        self.$triggerEvent('sourcechange', { event: false, data: {
            prevSource: oldValue,
            source    : newValue
        }});

        self._aT = true;
        self.$triggerEvent('loading', { event: false, data: { source: self.mdSource, hash }});

        newValue = newValue + (newValue.indexOf('?') !== -1 ? '&' : '?') + 'v=' + Date.now();

        fetch(newValue, {})
            .then(response => response.text() )
            .then(md => self.markdownData = md )
            .finally(_ => {
                self._aT = false;
                setTimeout(_ => self.$triggerEvent('loaded', { event: false, data: { source: self.mdSource, hash } }), 0);
            });
    }

    /**
     * Called when the markdownData property changes value
     * @param {string} oldValue the old value
     * @param {string} newValue the new value
     */
    onChanged_markdownData (oldValue, newValue) {
        let self = this;

        scriptPromise.then(_ => {
            let splitSource = self.mdSource.split('/');
            let source      = splitSource.splice(0, splitSource.length - 1).join('/') + '/';
            let html        = self._aV(newValue, { baseUrl: source, xhtml: true, smartLists: true, smartypants: true });
            //console.log('Source:', source)
            //self._aW = source;

            if (self._aU?.value) {
                self._aU.value.innerHTML = html;
                self._aU.value.scrollTop = 0;

                // Make sure external links are opened in a new tab
                for (let a of self._aU.value.getElementsByTagName('a')) {
                    let href = a.getAttribute('href');

                    if (!href.startsWith('#') && href.indexOf('.md') === -1 && href.indexOf('.mdown') === -1) {
                        a.target = '_aX';
                    }
                };

                if (!self.$attribs.turnOffHighlight) {
                    window.hljs?.highlightAll();
                }

                // Scroll to hashed location
                let hash = self.mdSource.split('#')[1];
                let el   = hash && document.getElementById(hash);

                if (el) {
                    //getScrollParentEl(el)?.scroll(0, el.offsetTop);
                    getScrollParentEl(el)?.scrollIntoView();
                }
            }
        });
    }

    /**
     * Called when an useShadowRoot changes value
     * @param {boolean} oldValue the old value
     * @param {boolean} newValue the new value
     */
    onChanged_useShadowRoot (oldValue, newValue) {
        this.options.useShadowRoot = newValue;
    }

    /**
     * Convert markdown data to (sanitized) html
     * @param {string} data
     * @param {Object} optionsOrCallback
     * @returns {string} the html
     */
     _aV(data, optionsOrCallback, callback) {
        let html = window.marked.parse(data, optionsOrCallback);

        return window.DOMPurify.sanitize(html);
    }

    /**
     *
     * @param {HTMLElement} e
     */
    _aY(e) {
        let self = this;

        if (!e) {
            return;
        }
        let el     = e.target;
        let isLink = el.localName === 'a';

        if (!isLink) {
            return;
        }

        let href = el.getAttribute('href');

        if (el.target === '_aX') {
            return;
        }

        e.stopImmediatePropagation();
        e.preventDefault();
        e.stopPropagation();

        if (href.startsWith('#')) {
            let el = document.querySelector('wave-markdown ' + href)

            //el && getScrollParentEl(el)?.scroll(0, el.offsetTop);
            el && el.scrollIntoView();

            return;
        }

        let event = {
            a     : el,
            href  : el.href,
            cancel: false,
        };

        self.dispatchEvent(new CustomEvent('navigate', { detail: event }));

        if (!event.cancel) {
            self.mdSource = href;
        }
    }

    /** Render the template */
    render(reason) {
        let self = this;

        return html`<div style="position:relative;display:flex;flex-grow:1;overflow:hidden"><div style="overflow:auto" ${ref(self._aU)} @click="${self._aY}"></div>${self._aT && !self.$attribs.hideSpinner
                    ? html`<div style="background:rgba(127,127,127,.06);position:absolute;display:flex;inset:0"><div style="margin:auto"><div class="spinner accent120"><div class="spinner-dot spinner-dot--first"></div><div class="spinner-dot spinner-dot--second"></div><div class="spinner-dot spinner-dot--third"></div><div class="spinner-dot spinner-dot--fourth"></div></div></div></div>`
                    : nothing}</div>`;
    }
}

// Define the new web component
if (!window.customElements.get(tag)) {
    window.customElements.define(tag, WaveMarkdown);
}